import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Key, LIVE_COLORS, Stat } from "./RadarSpectrum";
import { timeTicks } from "./RadarWaterfall";
import { More, Segment } from "./radarUi";
import { rtctApi } from "services/rtctApi";
import { duration } from "utils/fieldFormat";
import {
  finite,
  formatMm as mm,
  sgtTime as sgt,
  timeOf,
} from "utils/radarDiagnostics";
import {
  PHASE_CAPTURES,
  WAVELENGTH_MM,
  listBeatSignals,
  phaseSeries,
} from "utils/radarPhase";
import { clipFrom } from "utils/radarSignal";
import { hasSpectrum, windowText } from "utils/radarStream";

// Micro-movement (Radar analysis): the change in the strongest echo's
// distance, far finer than a range cell, from the phase of the beat signals
// of the page's window - streamed, from bursts (rich readings among them:
// one beat signal a wake) or both. On demand: the beat signals are listed
// (radar/stream/?mode=), the newest 600 loaded one record at a time
// (radar-captures/<id>/, four at once, kept while the page is open) and
// analysed here (utils/radarPhase). A day of rich readings on a 10-minute
// wake is 144 beat signals, all of them; longer windows the newest 600.
const SOURCES = [
  ["all", "Both"],
  ["stream", "Live stream"],
  ["burst", "Bursts"],
];
const SOURCE_TEXT = {
  all: "streamed and burst",
  stream: "streamed",
  burst: "burst",
};
const WORKERS = 4;
// A day of rich readings (144 on a 10-minute wake) keeps its dots.
const DOTS_UP_TO = 200;
const um = (v, digits = 1) =>
  `${v.toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: digits })} µm`;
const signedUm = (v, digits = 1) =>
  `${v > 0 ? "+" : v < 0 ? "−" : "±"}${um(Math.abs(v), digits)}`;
const clock = (ms, withDay) =>
  new Intl.DateTimeFormat("en-SG", {
    timeZone: "Asia/Singapore",
    ...(withDay ? { day: "numeric", month: "short" } : {}),
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(ms));

const PhaseChart = memo(function PhaseChart({ result }) {
  const span = result.to - result.from;
  const rows = useMemo(
    () =>
      result.points.map((p) => ({
        ms: p.ms,
        um: p.um,
        trend: result.trend
          ? result.trend.intercept +
            (result.trend.umPerHour * (p.ms - result.from)) / 3600000
          : null,
      })),
    [result],
  );
  const minutes = Math.max(15, span / 60000);
  return (
    <div className="field-phase-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={rows}
          margin={{ top: 10, right: 14, bottom: 0, left: 0 }}
        >
          <CartesianGrid stroke="#e8eff1" vertical={false} />
          <XAxis
            dataKey="ms"
            type="number"
            domain={[result.from, Math.max(result.to, result.from + 60000)]}
            ticks={timeTicks(result.from, result.to, minutes)}
            tickFormatter={(v) => clock(v, span > 86400000)}
            tick={{ fontSize: 10, fill: "#6d8490" }}
          />
          <YAxis
            tickFormatter={(v) => Math.round(v).toLocaleString()}
            tick={{ fontSize: 10, fill: "#6d8490" }}
            unit=" µm"
            width={72}
          />
          <Tooltip
            labelFormatter={(v) => sgt(new Date(v).toISOString())}
            formatter={(value, name) => [
              value === null || value === undefined ? "—" : signedUm(value),
              name,
            ]}
          />
          <Line
            dataKey="um"
            name="Range change"
            stroke={LIVE_COLORS.ours}
            strokeWidth={1.4}
            dot={
              rows.length <= DOTS_UP_TO
                ? { r: 1.8, stroke: "none", fill: LIVE_COLORS.ours }
                : false
            }
            isAnimationActive={false}
          />
          <Line
            dataKey="trend"
            name="Linear trend"
            stroke={LIVE_COLORS.radar}
            strokeWidth={1.6}
            strokeDasharray="6 4"
            dot={false}
            activeDot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
});

export default function RadarPhase({ mac, minutes = 1440 }) {
  const [source, setSource] = useState("all");
  const [unclipped, setUnclipped] = useState(false);
  const [run, setRun] = useState(null);
  const [stage, setStage] = useState("idle");
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(null);
  const cache = useRef(new Map());
  const abort = useRef(null);
  useEffect(() => () => abort.current?.abort(), []);

  const measure = async () => {
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    const signal = controller.signal;
    const asked = { minutes, source };
    setRun(asked);
    setError("");
    setStage("listing");
    setProgress({ done: 0, total: 0 });
    try {
      const listed = await listBeatSignals(
        (since) =>
          rtctApi.radarStream(
            mac,
            signal,
            since
              ? { since, limit: 300, mode: source }
              : { limit: 300, mode: source },
          ),
        { from: Date.now() - minutes * 60000 },
      );
      if (signal.aborted) return;
      const rows = listed.rows.slice(-PHASE_CAPTURES);
      setStage("loading");
      setProgress({ done: 0, total: rows.length });
      const queue = rows.filter((r) => !cache.current.has(String(r.id)));
      let done = rows.length - queue.length;
      setProgress({ done, total: rows.length });
      const worker = async () => {
        while (queue.length && !signal.aborted) {
          const row = queue.shift();
          try {
            const data = await rtctApi.radarCapture(row.id, signal);
            if (signal.aborted) return;
            // Only what the analysis needs is kept.
            cache.current.set(String(row.id), {
              adc: data?.adc,
              clip: data?.clip,
              step: data?.range_step_mm,
              cal: data?.context?.cfg?.cal_mm,
            });
          } catch (e) {
            if (signal.aborted) return;
            cache.current.set(String(row.id), { error: e.message });
          }
          done += 1;
          if (done % 5 === 0 || !queue.length)
            setProgress({ done, total: rows.length });
        }
      };
      await Promise.all(Array.from({ length: WORKERS }, worker));
      if (signal.aborted) return;
      setLoaded({
        ...asked,
        listed: listed.rows.length,
        complete: listed.complete,
        rows,
        step: listed.step ?? null,
      });
      setStage("done");
    } catch (e) {
      if (!signal.aborted) {
        setError(
          e.status === 404
            ? "This server does not list radar captures by kind yet; the measurement needs the updated backend."
            : `The beat signals could not load: ${e.message}`,
        );
        setStage("idle");
      }
    }
  };

  // The loaded beat signals as the analysis takes them.
  const captures = useMemo(
    () =>
      (loaded?.rows || [])
        .map((row) => {
          const data = cache.current.get(String(row.id));
          const samples = Array.isArray(data?.adc)
            ? data.adc.map(Number).filter(Number.isFinite)
            : null;
          return samples && samples.length >= 8
            ? {
                id: row.id,
                ms: timeOf(row.t),
                samples,
                clip: clipFrom(data, samples),
                // A spectrum's step: a beat signal alone carries the
                // server's assumed 30 mm (listBeatSignals()).
                step:
                  (hasSpectrum(row) ? finite(row.step_mm) : null) ??
                  loaded.step ??
                  finite(row.step_mm) ??
                  finite(data.step),
                cal: finite(row.cal_mm) ?? finite(data.cal) ?? 0,
              }
            : null;
        })
        .filter(Boolean),
    [loaded],
  );
  const failed = (loaded?.rows.length || 0) - captures.length;
  const result = useMemo(
    () => (captures.length ? phaseSeries(captures, { unclipped }) : null),
    [captures, unclipped],
  );
  const busy = stage === "listing" || stage === "loading";
  const stale =
    loaded && (loaded.minutes !== minutes || loaded.source !== source);
  const [chip, tone] = busy
    ? ["Measuring", "is-waiting"]
    : result
      ? [`${result.used.toLocaleString()} beat signals`, "is-running"]
      : loaded
        ? ["No beat signals", "is-off"]
        : ["Not measured yet", "is-off"];
  const spanHours = result ? (result.to - result.from) / 3600000 : 0;

  return (
    <section
      className="field-panel field-phase"
      aria-labelledby="radar-phase-title"
    >
      <div className="field-panel-head">
        <div>
          <h2 id="radar-phase-title">Micro-movement (phase)</h2>
          <p>
            Changes in the echo's distance far smaller than a range cell, from
            the phase of the beat signals · the last {windowText(minutes)}
          </p>
        </div>
        <span className={`field-state ${tone}`} role="status">
          {chip}
        </span>
      </div>
      <div className="field-panel-body field-phase-body">
        <div className="field-phase-controls">
          <Segment
            label="Beat signals from"
            options={SOURCES}
            value={source}
            onChange={setSource}
            disabled={busy}
          />
          <label className="field-burst-toggle">
            <input
              type="checkbox"
              checked={unclipped}
              onChange={(e) => setUnclipped(e.target.checked)}
            />
            Unclipped part only
            <small>
              Each beat signal from its last clipped sample on, with its own
              window: fewer samples, no flat tops.
            </small>
          </label>
          <div className="field-actions" style={{ marginTop: 0 }}>
            <button
              type="button"
              className="field-button primary"
              disabled={busy}
              onClick={measure}
            >
              {busy ? "Measuring…" : loaded ? "Measure again" : "Measure"}
            </button>
          </div>
        </div>
        {busy && (
          <div className="field-phase-progress" role="status">
            <span>
              {stage === "listing"
                ? `Listing the ${SOURCE_TEXT[run.source]} captures of the last ${windowText(run.minutes)}…`
                : `Loading beat signals: ${progress.done.toLocaleString()} of ${progress.total.toLocaleString()}`}
            </span>
            {stage === "loading" && (
              <progress
                aria-label="Beat signals loaded"
                max={Math.max(1, progress.total)}
                value={progress.done}
              />
            )}
          </div>
        )}
        {error && (
          <div className="field-alert" role="alert">
            {error}
          </div>
        )}
        {!loaded && !busy && !error && (
          <More label="What Measure does">
            <p className="field-caption" style={{ margin: 0 }}>
              Measure loads the newest {PHASE_CAPTURES} beat signals of the
              window (about 8 kB each) and follows the phase of their strongest
              echo: a still surface holds its phase to a fraction of a degree, a
              fraction of a micrometre. Rich readings bring one beat signal a
              wake (144 a day on a 10-minute wake): a day's window uses every
              one of them, a longer one the newest {PHASE_CAPTURES} (about four
              days).
            </p>
          </More>
        )}
        {stale && !busy && (
          <small className="field-radar-hint">
            Measured over the last {windowText(loaded.minutes)} from{" "}
            {SOURCE_TEXT[loaded.source]} beat signals; measure again for the
            window and source chosen now.
          </small>
        )}
        {loaded && !busy && !result && (
          <div className="field-radar-empty">
            No beat signals with a usable echo
            {loaded.rows.length
              ? ` among the ${loaded.rows.length.toLocaleString()} loaded`
              : ` in the last ${windowText(loaded.minutes)}`}
            . Beat signals come with Beat signal ticked in Radar rich data on
            Node Control, in raw output mode 2 or alternating.
          </div>
        )}
        {result && !busy && (
          <>
            <div className="field-stream-readout">
              <Stat
                label="Echo followed"
                value={result.mm !== null ? mm(result.mm, 1) : "—"}
                detail={`Bin ${result.bin.toFixed(3)}${result.step !== null ? ` × ${result.step.toFixed(2)} mm − ${mm(result.cal)}` : ""}: the strongest beyond the near field, median over the captures`}
              />
              <Stat
                label="Trend"
                value={
                  result.trend
                    ? `${signedUm(result.trend.umPerHour)}/hour`
                    : "—"
                }
                detail={
                  result.trend
                    ? `${signedUm(result.trend.umPerHour * spanHours, 0)} over ${duration(spanHours * 3600)} · least squares`
                    : "Needs two captures or more"
                }
              />
              <Stat
                label="Noise per capture"
                value={
                  result.noise
                    ? `${result.noise.deg.toFixed(2)}° = ${um(result.noise.um, 2)}`
                    : "—"
                }
                detail="Spread of the steps between consecutive captures, less the drift, over √2"
              />
              <Stat
                label="Beat signals"
                value={result.used.toLocaleString()}
                detail={[
                  `${sgt(new Date(result.from).toISOString())} to ${sgt(new Date(result.to).toISOString())}`,
                  loaded.listed > loaded.rows.length
                    ? `the newest ${loaded.rows.length.toLocaleString()} of ${loaded.listed.toLocaleString()}`
                    : null,
                  result.skipped
                    ? `${result.skipped} with too few samples after their clipping`
                    : null,
                  failed ? `${failed} could not load` : null,
                  loaded.complete ? null : "the listing stopped early",
                ]
                  .filter(Boolean)
                  .join(" · ")}
              />
            </div>
            {result.risky > 0 && (
              <div className="field-radar-caution" role="note">
                {result.risky === 1
                  ? "One step between consecutive captures is"
                  : `${result.risky} steps between consecutive captures are`}{" "}
                more than a quarter turn (0.47 mm): there the change may have
                wrapped, and the phase cannot tell it from a change the other
                way. Trust the series where the captures are close together.
              </div>
            )}
            <PhaseChart result={result} />
            <ul className="field-radar-legend" aria-label="Micro-movement key">
              <Key color={LIVE_COLORS.ours}>
                Range change since the first capture
              </Key>
              <Key kind="dashed" color={LIVE_COLORS.radar}>
                Linear trend
              </Key>
            </ul>
            <More label="How to read it">
              <p className="field-caption">
                Range change = −(phase − first phase) × λ / 4π, with λ ={" "}
                {WAVELENGTH_MM} mm (80 GHz): one turn of phase is 1.87 mm, one
                degree 5.2 µm. The sign is relative; its direction is to be
                confirmed with a known level change. The phase wraps every 1.87
                mm, so only changes under about 0.9 mm between captures are
                followed. Ripple or rain scramble it, another echo in the same
                cell biases it, and bursts and rich readings come from separate
                wakes (a rich reading is one beat signal a wake, 10 minutes
                apart on a 10-minute wake): if powering the radar up again
                resets its phase (still to be tested), a jump between wakes is
                not movement.
              </p>
            </More>
          </>
        )}
      </div>
    </section>
  );
}
